"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Avatar,
  CreateDraft,
  Platform,
  Product,
  Scene,
  Script,
  VideoStyle,
  Voice,
} from "@easyshot/shared";
import {
  ApiError,
  AVATARS,
  CHATTERBOX_PRESET_VOICE_IDS,
  PLATFORM_PRESETS,
  RATIO_CSS,
  STYLE_OPTIONS,
  VOICES,
} from "@easyshot/shared";
import { api } from "@/lib/api";
import {
  uploadAvatarMedia,
  type UploadedAvatarMedia,
} from "@/lib/avatar-media-upload";
import { AvatarVideo } from "./avatar-video";
import { pickSampleVideo } from "@/lib/sample-media";
import { ProductMedia, type ProductMediaItem } from "./product-media";
import {
  buildInfiniteTalkProduct,
  buildInfiniteTalkScript,
  estimateInfiniteTalkDuration,
} from "@/lib/infinitetalk-flow";
import {
  findInfiniteTalkPresetPerson,
  INFINITETALK_PRESET_PEOPLE,
} from "@/lib/infinitetalk-presets";
import {
  TokenCostDialog,
  type TokenEstimateView,
} from "./token-cost-dialog";
import {
  Badge,
  Button,
  Card,
  Field,
  Spinner,
  cn,
  inputClass,
} from "./ui";
import { useCurrency } from "./currency-provider";

const STEPS = ["Product", "Avatar & Platforms", "Script", "Generate"] as const;
const CHATTERBOX_LANGUAGE_OPTIONS = [
  ["en", "English"], ["zh", "Chinese"], ["ja", "Japanese"], ["ko", "Korean"],
  ["es", "Spanish"], ["fr", "French"], ["de", "German"], ["it", "Italian"],
  ["pt", "Portuguese"], ["ar", "Arabic"], ["da", "Danish"], ["el", "Greek"],
  ["fi", "Finnish"], ["he", "Hebrew"], ["hi", "Hindi"], ["ms", "Malay"],
  ["nl", "Dutch"], ["no", "Norwegian"], ["pl", "Polish"], ["ru", "Russian"],
  ["sv", "Swedish"], ["sw", "Swahili"], ["tr", "Turkish"],
] as const;

/** 后台可增删的工作流模板（/api/templates），种子数据用内置目录兜底 */
interface WizardTemplate {
  id: string;
  name: string;
  description: string;
  avatarIds: string[];
  voiceIds: string[];
  provider?: { type: string; engine?: string };
  capabilities?: {
    avatarMediaKinds?: Array<"photo" | "video">;
  };
}

interface VideoEstimateView {
  durationSec: number;
  videoCount: number;
  totalCents: number;
  items: Array<{ platform: Platform; chargeCents: number }>;
}

interface AvatarUploadOption extends UploadedAvatarMedia {
  ownedByCurrentUser: boolean;
}

interface ClonedVoiceOption extends Voice {
  cloned: true;
}

const INFINITETALK_SOURCE_TYPES: Record<"photo" | "video", Set<string>> = {
  photo: new Set(["image/jpeg", "image/png", "image/webp"]),
  video: new Set(["video/mp4", "video/quicktime"]),
};

const FALLBACK_TEMPLATES: WizardTemplate[] = [
  ...STYLE_OPTIONS.map((s) => ({
    id: s.id,
    name: s.label,
    description: s.description,
    avatarIds: [],
    voiceIds: [],
    provider: {
      type:
        s.id === "motion" || s.id === "faceless" || s.id === "comment_ad"
          ? "seedance"
          : "heygen",
    },
  })),
  {
    id: "infinitetalk_avatar",
    name: "InfiniteTalk Avatar Video",
    description:
      "Turn an approved customer photo into a natural talking video with InfiniteTalk.",
    avatarIds: [],
    voiceIds: CHATTERBOX_PRESET_VOICE_IDS,
    provider: { type: "infinitetalk", engine: "infinitetalk-auto" },
    // Hosted fallback is photo-only; the API expands this to video when the
    // self-hosted RunPod backend is active.
    capabilities: { avatarMediaKinds: ["photo"] },
  },
];

function isFacelessStyle(styleId: string): boolean {
  return styleId === "faceless";
}

function productAnalysisText(product: Product): string {
  return [
    product.title,
    product.brand ? `Brand: ${product.brand}` : "",
    product.price ? `Price: ${product.price}` : "",
    product.description,
    product.sellingPoints.length
      ? `Selling points:\n${product.sellingPoints.map((point) => `- ${point}`).join("\n")}`
      : "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

export function CreateWizard() {
  const { currency, formatUsdCents, exactUsdCents } = useCurrency();
  const router = useRouter();
  const params = useSearchParams();
  const presetUrl = params.get("url") ?? "";
  const presetStyle = params.get("style") ?? "";
  const resumeDraftId = params.get("draft") ?? "";

  const [step, setStep] = useState(0);
  const [url, setUrl] = useState(presetUrl);
  const [text, setText] = useState("");
  const [product, setProduct] = useState<Product | null>(null);

  // 落地页模板卡片带 ?style= 预选；无效 id 由模板拉取后的兜底纠正
  const [style, setStyle] = useState<VideoStyle>(presetStyle || "ugc");
  const [platforms, setPlatforms] = useState<Platform[]>(["tiktok"]);
  const [avatarId, setAvatarId] = useState(
    isFacelessStyle(presetStyle) ? "av_none" : "av_mia",
  );
  const [voiceId, setVoiceId] = useState("vo_emma");

  // 动态目录：后台增删模板/头像/声音后网站即时生效
  const [templates, setTemplates] = useState<WizardTemplate[]>(FALLBACK_TEMPLATES);
  const [avatars, setAvatars] = useState<Avatar[]>([...AVATARS]);
  const [voices, setVoices] = useState<Voice[]>([...VOICES]);
  const [avatarUploads, setAvatarUploads] = useState<AvatarUploadOption[]>([]);
  const avatarPhotoInput = useRef<HTMLInputElement>(null);
  const [avatarConsentAccepted, setAvatarConsentAccepted] = useState(false);
  const [avatarUploadBusy, setAvatarUploadBusy] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [clonedVoices, setClonedVoices] = useState<ClonedVoiceOption[]>([]);
  const voiceCloneInput = useRef<HTMLInputElement>(null);
  const [showVoiceClone, setShowVoiceClone] = useState(false);
  const [voiceCloneName, setVoiceCloneName] = useState("My voice");
  const [voiceCloneFile, setVoiceCloneFile] = useState<File | null>(null);
  const [voiceCloneLanguage, setVoiceCloneLanguage] = useState("en");
  const [voiceCloneConsent, setVoiceCloneConsent] = useState(false);
  const [voiceCloneBusy, setVoiceCloneBusy] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState<string | null>(null);
  const previewAudio = useRef<HTMLAudioElement | null>(null);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [voicePreviewUrls, setVoicePreviewUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    void fetch("/api/templates")
      .then((r) => r.json())
      .then((t: WizardTemplate[]) => {
        if (!alive || !t.length) return;
        setTemplates(t);
        setStyle((s) => (t.some((x) => x.id === s) ? s : t[0].id));
      })
      .catch(() => {});
    void fetch("/api/catalog")
      .then((r) => r.json())
      .then((c: { avatars: Avatar[]; voices: Voice[] }) => {
        if (!alive) return;
        if (c.avatars?.length) {
          setAvatars(c.avatars);
          setAvatarId((id) =>
            c.avatars.some((a) => a.id === id) ? id : c.avatars[0].id,
          );
        }
        if (c.voices?.length) {
          setVoices(c.voices);
          setVoiceId((id) =>
            c.voices.some((v) => v.id === id) ? id : c.voices[0].id,
          );
        }
      })
      .catch(() => {});
    void fetch("/api/avatars/uploads")
      .then((r) => (r.ok ? r.json() : []))
      .then((uploads: AvatarUploadOption[]) => {
        if (alive && Array.isArray(uploads)) setAvatarUploads(uploads);
      })
      .catch(() => {});
    void fetch("/api/voices/clones")
      .then((r) => (r.ok ? r.json() : []))
      .then((items: ClonedVoiceOption[]) => {
        if (alive && Array.isArray(items)) setClonedVoices(items);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const activeTemplate = templates.find((t) => t.id === style);
  const facelessStyle = isFacelessStyle(style);
  const h3Style = activeTemplate?.provider?.type === "minimax_h3";
  const presenterlessStyle = facelessStyle || h3Style;
  const infiniteTalkStyle = activeTemplate?.provider?.type === "infinitetalk";
  const infiniteTalkSourceKinds = activeTemplate?.capabilities
    ?.avatarMediaKinds ?? ["photo"];
  const infiniteTalkAcceptsPhoto = infiniteTalkSourceKinds.includes("photo");
  const infiniteTalkAcceptsVideo = infiniteTalkSourceKinds.includes("video");
  const isCompatibleInfiniteTalkUpload = useCallback(
    (upload: AvatarUploadOption) =>
      (upload.kind === "photo"
        ? infiniteTalkAcceptsPhoto
        : infiniteTalkAcceptsVideo) &&
      INFINITETALK_SOURCE_TYPES[upload.kind].has(upload.mime),
    [infiniteTalkAcceptsPhoto, infiniteTalkAcceptsVideo],
  );
  const approvedAvatarUploads = avatarUploads.filter(
    (upload) =>
      upload.ownedByCurrentUser &&
      upload.status === "ready" &&
      upload.consentStatus === "approved" &&
      isCompatibleInfiniteTalkUpload(upload),
  );
  const infiniteTalkAvatarReady =
    !infiniteTalkStyle ||
    Boolean(findInfiniteTalkPresetPerson(avatarId)) ||
    approvedAvatarUploads.some((upload) => upload.id === avatarId);

  async function uploadInfiniteTalkAvatar(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!avatarConsentAccepted) {
      setAvatarUploadError(
        "Confirm that you have permission to animate this person first",
      );
      return;
    }
    setAvatarUploadBusy(true);
    setAvatarUploadError(null);
    try {
      const uploaded = await uploadAvatarMedia(file, true);
      const option: AvatarUploadOption = {
        ...uploaded,
        ownedByCurrentUser: true,
      };
      setAvatarUploads((current) => [
        option,
        ...current.filter((item) => item.id !== option.id),
      ]);
      setAvatarId(option.id);
      setAvatarConsentAccepted(false);
    } catch (reason) {
      setAvatarUploadError(
        reason instanceof Error ? reason.message : "Avatar upload failed",
      );
    } finally {
      setAvatarUploadBusy(false);
      if (avatarPhotoInput.current) avatarPhotoInput.current.value = "";
    }
  }

  useEffect(() => {
    if (presenterlessStyle) setAvatarId("av_none");
  }, [presenterlessStyle]);

  useEffect(() => {
    if (!infiniteTalkStyle) return;
    const approved = avatarUploads.filter(
      (upload) =>
        upload.ownedByCurrentUser &&
        upload.status === "ready" &&
        upload.consentStatus === "approved" &&
        isCompatibleInfiniteTalkUpload(upload),
    );
    if (
      !findInfiniteTalkPresetPerson(avatarId) &&
      !approved.some((upload) => upload.id === avatarId)
    ) {
      setAvatarId(approved[0]?.id ?? INFINITETALK_PRESET_PEOPLE[0].id);
    }
  }, [
    infiniteTalkStyle,
    avatarUploads,
    avatarId,
    isCompatibleInfiniteTalkUpload,
  ]);

  // 模板可限定头像/声音池；空数组 = 不限
  const availableAvatars = activeTemplate?.avatarIds.length
    ? avatars.filter((a) => activeTemplate.avatarIds.includes(a.id))
    : avatars;
  const availableVoices = activeTemplate?.voiceIds.length
    ? [
        ...voices.filter((v) => activeTemplate.voiceIds.includes(v.id)),
        ...clonedVoices,
      ]
    : [...voices, ...clonedVoices];

  // A template change can replace the entire voice pool. Keep the controlled
  // select value aligned with the option users actually see; otherwise React
  // displays the first option while preview/generation still submits the stale
  // voice from the previous template.
  useEffect(() => {
    if (
      availableVoices.length > 0 &&
      !availableVoices.some((voice) => voice.id === voiceId)
    ) {
      setVoiceId(availableVoices[0].id);
    }
  }, [availableVoices, voiceId]);

  /**
   * 风格卡三态：未选定露两行 → 选定后只留选中那张 → 展开时全部露出。
   * 带 ?style= 进来说明已经在落地页挑过了，直接算选定。
   */
  const [stylePicked, setStylePicked] = useState(Boolean(presetStyle));
  const [showAllStyles, setShowAllStyles] = useState(false);

  useEffect(() => {
    if (infiniteTalkStyle && stylePicked && step > 1) setStep(1);
  }, [infiniteTalkStyle, step, stylePicked]);

  const [script, setScript] = useState<Script | null>(null);
  const [infiniteTalkSpeech, setInfiniteTalkSpeech] = useState("");
  const [infiniteTalkBrief, setInfiniteTalkBrief] = useState("");
  const [removeAvatarBackground, setRemoveAvatarBackground] = useState(false);
  const [infiniteTalkBackground, setInfiniteTalkBackground] = useState<
    ProductMediaItem[]
  >([]);
  const [variants, setVariants] = useState(2);
  const [varyHooks, setVaryHooks] = useState(true);
  const [varyAvatars, setVaryAvatars] = useState(false);

  // 用户自传的商品照片/视频（可选）；照片并入 product.images，视频进 product.videos
  const [media, setMedia] = useState<ProductMediaItem[]>([]);
  const [analyzedInput, setAnalyzedInput] = useState<string | null>(null);

  const applyMedia = useCallback(
    (p: Product, items: ProductMediaItem[]): Product => {
      const isUpload = (u: string) =>
        u.includes("/api/uploads/") || u.includes("/product-uploads/");
      const photos = items.filter((m) => m.kind === "photo").map((m) => m.url);
      const videos = items.filter((m) => m.kind === "video").map((m) => m.url);
      return {
        ...p,
        images: [...p.images.filter((u) => !isUpload(u)), ...photos],
        videos,
      };
    },
    [],
  );

  const onMediaChange = useCallback(
    (items: ProductMediaItem[]) => {
      setMedia(items);
      setProduct((p) => (p ? applyMedia(p, items) : p));
    },
    [applyMedia],
  );

  const [busy, setBusy] = useState<
    null | "scrape" | "media" | "script" | "submit"
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [needCredits, setNeedCredits] = useState(false);
  const [tokenEstimate, setTokenEstimate] = useState<TokenEstimateView | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [videoEstimate, setVideoEstimate] = useState<VideoEstimateView | null>(null);
  const [estimatingVideo, setEstimatingVideo] = useState(false);
  const pendingTokenAction = useRef<null | (() => Promise<void>)>(null);
  const [draftId, setDraftId] = useState(resumeDraftId);
  const [leaveTarget, setLeaveTarget] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const hasProgress = Boolean(
    url.trim() ||
      text.trim() ||
      product ||
      media.length ||
      infiniteTalkBackground.length ||
      infiniteTalkSpeech.trim() ||
      infiniteTalkBrief.trim() ||
      script ||
      step > 0 ||
      stylePicked,
  );

  useEffect(() => {
    if (!resumeDraftId) return;
    let alive = true;
    void api.getDraft(resumeDraftId).then((draft: CreateDraft) => {
      if (!alive) return;
      const p = draft.payload as {
        url?: string; text?: string; product?: Product | null; style?: VideoStyle;
        platforms?: Platform[]; avatarId?: string; voiceId?: string;
        script?: Script | null; media?: ProductMediaItem[]; variants?: number;
        varyHooks?: boolean; varyAvatars?: boolean; stylePicked?: boolean;
        infiniteTalkSpeech?: string; infiniteTalkBrief?: string;
        infiniteTalkBackground?: ProductMediaItem[];
        removeAvatarBackground?: boolean;
      };
      setStep(draft.step);
      setUrl(p.url ?? "");
      setText(p.text ?? "");
      setProduct(p.product ?? null);
      if (p.style) setStyle(p.style);
      if (p.platforms?.length) setPlatforms(p.platforms);
      if (p.avatarId) setAvatarId(p.avatarId);
      if (p.voiceId) setVoiceId(p.voiceId);
      setScript(p.script ?? null);
      setMedia(p.media ?? []);
      setInfiniteTalkSpeech(
        p.infiniteTalkSpeech ??
          p.script?.scenes.map((scene) => scene.narration).join(" ") ??
          "",
      );
      setInfiniteTalkBackground(p.infiniteTalkBackground ?? []);
      setInfiniteTalkBrief(p.infiniteTalkBrief ?? "");
      setRemoveAvatarBackground(Boolean(p.removeAvatarBackground));
      if (p.variants) setVariants(p.variants);
      if (typeof p.varyHooks === "boolean") setVaryHooks(p.varyHooks);
      if (typeof p.varyAvatars === "boolean") setVaryAvatars(p.varyAvatars);
      setStylePicked(p.stylePicked ?? true);
    }).catch(() => setError("Could not restore this draft"));
    return () => { alive = false; };
  }, [resumeDraftId]);

  useEffect(() => {
    if (!product || !script) {
      setVideoEstimate(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setEstimatingVideo(true);
      void fetch("/api/video/estimate", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ product, script, style, platforms, variants }),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Could not calculate video price");
          return response.json() as Promise<VideoEstimateView>;
        })
        .then(setVideoEstimate)
        .catch((reason: unknown) => {
          if (!(reason instanceof DOMException && reason.name === "AbortError")) {
            setVideoEstimate(null);
            setError(reason instanceof Error ? reason.message : "Could not calculate video price");
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setEstimatingVideo(false);
        });
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [product, script, style, platforms, variants]);

  useEffect(() => {
    const onLeave = (event: Event) => {
      if (!hasProgress) return;
      event.preventDefault();
      setLeaveTarget((event as CustomEvent<{ href: string }>).detail.href);
    };
    window.addEventListener("easyshot:create-leave", onLeave);
    return () => window.removeEventListener("easyshot:create-leave", onLeave);
  }, [hasProgress]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasProgress) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasProgress]);

  async function saveDraftAndLeave() {
    if (!leaveTarget) return;
    setSavingDraft(true);
    setError(null);
    try {
      const saved = await api.saveDraft({
        id: draftId || undefined,
        name: product?.title || text.trim().slice(0, 60) || "Untitled video",
        step,
        payload: {
          url, text, product, style, platforms, avatarId, voiceId, script, media,
          variants, varyHooks, varyAvatars, stylePicked, infiniteTalkSpeech,
          infiniteTalkBrief, infiniteTalkBackground, removeAvatarBackground,
        },
      });
      setDraftId(saved.id);
      router.push(leaveTarget);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save draft");
      setLeaveTarget(null);
    } finally {
      setSavingDraft(false);
    }
  }

  async function requestTokenEstimate(
    payload: Record<string, unknown>,
    action: () => Promise<void>,
  ) {
    setError(null);
    setEstimating(true);
    try {
      const response = await fetch("/api/usage/estimate", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Could not calculate token estimate");
      pendingTokenAction.current = action;
      setTokenEstimate((await response.json()) as TokenEstimateView);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not calculate token estimate");
    } finally {
      setEstimating(false);
    }
  }

  async function confirmTokenAction() {
    const action = pendingTokenAction.current;
    if (!action) return;
    setTokenEstimate(null);
    pendingTokenAction.current = null;
    await action();
  }

  const scrape = useCallback(
    async (value?: string, advance = true) => {
      setError(null);
      setBusy("scrape");
      try {
        const activeUrl = value ?? url;
        const p = activeUrl.trim()
          ? await api.scrape(activeUrl)
          : await postText(text);
        const analyzedText = activeUrl.trim() ? productAnalysisText(p) : text;
        setProduct(applyMedia(p, media));
        if (activeUrl.trim()) setText(analyzedText);
        setAnalyzedInput(`${activeUrl.trim()}\n${analyzedText.trim()}`);
        if (advance) setStep(1);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to parse product");
      } finally {
        setBusy(null);
      }
    },
    [url, text, media, applyMedia],
  );

  async function analyzeMedia() {
    setError(null);
    setBusy("media");
    try {
      const response = await fetch("/api/products/analyze-media", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ids: media.map((item) => item.id) }),
      });
      const payload = (await response.json().catch(() => ({}))) as
        | Product
        | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error : "Media analysis failed");
      }
      const analyzed = payload as Product;
      const analyzedText = productAnalysisText(analyzed);
      setProduct(applyMedia(analyzed, media));
      setText(analyzedText);
      setAnalyzedInput(`${url.trim()}\n${analyzedText.trim()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Media analysis failed");
    } finally {
      setBusy(null);
    }
  }

  /** 只上传了素材、没给链接/描述：跳过解析，起草空商品到 Step 2 手动完善 */
  function nextWithMediaOnly() {
    const draft: Product = {
      id: `pd_${Date.now().toString(36)}`,
      title: "My product",
      description: "",
      sellingPoints: [],
      images: [],
    };
    setProduct(applyMedia(draft, media));
    setStep(1);
  }

  async function nextFromProduct() {
    const currentInput = `${url.trim()}\n${text.trim()}`;
    if (product && analyzedInput === currentInput) {
      setProduct(applyMedia(product, media));
      setStep(1);
      return;
    }
    if (url.trim() || text.trim()) {
      await scrape(undefined, true);
      return;
    }
    nextWithMediaOnly();
  }

  const genScript = useCallback(async () => {
    if (!product) return;
    setError(null);
    setBusy("script");
    try {
      const s = await api.generateScript({
        product,
        style,
        platform: platforms[0] ?? "tiktok",
      });
      setScript(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Script generation failed");
    } finally {
      setBusy(null);
    }
  }, [product, style, platforms]);

  async function submit() {
    if (!product || !script) return;
    if (!infiniteTalkAvatarReady) {
      setError(
        "Select avatar media with approved consent before using InfiniteTalk",
      );
      return;
    }
    setError(null);
    setNeedCredits(false);
    setBusy("submit");
    try {
      const project = await api.createProject({
        name: product.title,
        product,
        script,
        style,
        avatarId,
        voiceId,
        platforms,
      });
      await api.generate(project.id, {
        platforms,
        variants,
        varyHooks,
        // 无数字人出镜时轮换头像无意义
        varyAvatars:
          avatarId === "av_none" || infiniteTalkStyle ? false : varyAvatars,
      });
      if (draftId) await api.deleteDraft(draftId).catch(() => undefined);
      router.push(`/studio/projects/${project.id}`);
    } catch (e) {
      setNeedCredits(e instanceof ApiError && e.status === 402);
      setError(e instanceof Error ? e.message : "Submission failed");
      setBusy(null);
    }
  }

  const totalOutputs = videoEstimate?.videoCount ?? platforms.length * variants;
  const totalChargeCents = videoEstimate?.totalCents ?? 0;
  const infiniteTalkMode = infiniteTalkStyle && stylePicked;
  const visibleSteps = infiniteTalkMode
    ? (["Setup", "Generate"] as const)
    : STEPS;

  function prepareInfiniteTalkReview() {
    if (!infiniteTalkAvatarReady || !infiniteTalkSpeech.trim()) return;
    const nextProduct = buildInfiniteTalkProduct(
      product,
      infiniteTalkBackground[0],
      removeAvatarBackground,
    );
    const nextScript = buildInfiniteTalkScript(infiniteTalkSpeech);
    setProduct(nextProduct);
    setScript(nextScript);
    setPlatforms(["tiktok"]);
    setVariants(1);
    setVaryHooks(false);
    setVaryAvatars(false);
    setStep(1);
  }

  async function generateInfiniteTalkSpeech() {
    const brief = infiniteTalkBrief.trim();
    if (!brief) return;
    setError(null);
    setBusy("script");
    try {
      const generated = await api.generateScript({
        product: {
          id: `pd_${Date.now().toString(36)}`,
          title: brief.split(/\n|[.!?。！？]/)[0]?.slice(0, 100) || "Talking avatar",
          description: brief,
          sellingPoints: brief
            .split(/\n|[;；]/)
            .map((line) => line.trim())
            .filter(Boolean)
            .slice(0, 12),
          images: [],
        },
        style,
        platform: "tiktok",
      });
      const editable = [
        generated.hook,
        ...generated.scenes.map((scene) => scene.narration),
        generated.cta,
      ]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" ");
      setInfiniteTalkSpeech(editable.slice(0, 2_000));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Script generation failed");
    } finally {
      setBusy(null);
    }
  }

  async function cloneVoice() {
    if (!voiceCloneFile || !voiceCloneConsent) return;
    setVoiceCloneBusy(true);
    setVoiceMessage(null);
    try {
      const endpoint = "/api/voices/clones";
      let response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          filename: voiceCloneFile.name,
          mime: voiceCloneFile.type,
          size: voiceCloneFile.size,
          name: voiceCloneName.trim() || "My voice",
          language: voiceCloneLanguage,
          consentAccepted: true,
        }),
      });
      if (response.status === 400) {
        const form = new FormData();
        form.set("file", voiceCloneFile);
        form.set("name", voiceCloneName.trim() || "My voice");
        form.set("language", voiceCloneLanguage);
        form.set("consentAccepted", "true");
        response = await fetch(endpoint, { method: "POST", body: form });
      } else if (response.ok) {
        const prepared = (await response.json()) as {
          uploadUrl: string;
          item: { id: string; mime: string };
        };
        const put = await fetch(prepared.uploadUrl, {
          method: "PUT",
          headers: { "content-type": prepared.item.mime },
          body: voiceCloneFile,
        });
        if (!put.ok) throw new Error("Direct voice upload failed");
        response = await fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ action: "complete", id: prepared.item.id }),
        });
      }
      const payload = (await response.json().catch(() => ({}))) as
        | ClonedVoiceOption
        | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error : "Voice cloning failed");
      }
      const cloned = payload as ClonedVoiceOption;
      setClonedVoices((current) => [
        cloned,
        ...current.filter((voice) => voice.id !== cloned.id),
      ]);
      setVoiceId(cloned.id);
      setShowVoiceClone(false);
      setVoiceCloneFile(null);
      setVoiceCloneConsent(false);
      setVoiceMessage("Chatterbox voice cloned and selected");
      if (voiceCloneInput.current) voiceCloneInput.current.value = "";
    } catch (reason) {
      setVoiceMessage(reason instanceof Error ? reason.message : "Voice cloning failed");
    } finally {
      setVoiceCloneBusy(false);
    }
  }

  async function previewVoice() {
    previewAudio.current?.pause();
    setPreviewingVoiceId(voiceId);
    setVoiceMessage(null);
    try {
      const selected = availableVoices.find((voice) => voice.id === voiceId);
      let audioUrl = selected?.preview;
      if (!audioUrl) {
        const response = await fetch("/api/voices/preview", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ voiceId }),
        });
        const payload = (await response.json().catch(() => ({}))) as {
          audioUrl?: string;
          error?: string;
        };
        if (!response.ok || !payload.audioUrl) {
          throw new Error(payload.error || "Voice preview failed");
        }
        audioUrl = payload.audioUrl;
      }
      setVoicePreviewUrls((current) => ({ ...current, [voiceId]: audioUrl }));
      setVoiceMessage("Preview ready — press play below");
    } catch (reason) {
      setVoiceMessage(reason instanceof Error ? reason.message : "Voice preview failed");
    } finally {
      setPreviewingVoiceId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      {leaveTarget && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-5 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-xl font-semibold">Save your Create progress?</h2>
            <p className="mt-2 text-sm leading-6 text-muted">
              You have unfinished work. Save it as a draft so you can continue later from Analytics.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="ghost" onClick={() => setLeaveTarget(null)} disabled={savingDraft}>
                Cancel
              </Button>
              <Button variant="secondary" onClick={() => router.push(leaveTarget)} disabled={savingDraft}>
                Leave without saving
              </Button>
              <Button onClick={() => void saveDraftAndLeave()} disabled={savingDraft}>
                {savingDraft ? <><Spinner className="size-4" /> Saving…</> : "Save & leave"}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {tokenEstimate && (
        <TokenCostDialog
          estimate={tokenEstimate}
          confirming={busy !== null}
          onCancel={() => {
            pendingTokenAction.current = null;
            setTokenEstimate(null);
          }}
          onConfirm={() => void confirmTokenAction()}
        />
      )}
      <div className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2">
        {visibleSteps.map((s, i) => (
          <button
            key={s}
            type="button"
            disabled={i > step}
            onClick={() => setStep(i)}
            className={cn(
              "flex items-center gap-2 text-sm transition disabled:cursor-not-allowed",
              i === step
                ? "text-foreground"
                : i < step
                  ? "text-muted hover:text-foreground"
                  : "text-white/25",
            )}
          >
            <span
              className={cn(
                "grid size-6 place-items-center rounded-full text-[11px] font-medium",
                i === step
                  ? "bg-gradient-to-br from-[var(--accent)] to-[var(--accent-2)] text-white"
                  : i < step
                    ? "bg-white/12"
                    : "bg-white/5",
              )}
            >
              {i < step ? "✓" : i + 1}
            </span>
            {s}
            {i < visibleSteps.length - 1 && (
              <span className="ml-1 text-white/15">—</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <Card className="mb-6 border-[var(--danger)]/40 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[#ffb3b3]">
          {error}
          {needCredits && (
            <a
              href="/#pricing"
              className="ml-2 font-medium text-[var(--accent-2)] hover:underline"
            >
              Add funds →
            </a>
          )}
        </Card>
      )}

      {/* InfiniteTalk uses a focused two-step flow: setup, then review/generate. */}
      {infiniteTalkMode && step === 0 && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Set up your talking avatar
              </h1>
              <p className="mt-2 text-sm text-muted">
                Add the person, background, voice, and exact words to speak.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setStylePicked(false);
                setShowAllStyles(true);
              }}
            >
              Change video type
            </Button>
          </div>

          <Card className="p-6">
            <div className="flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                1
              </span>
              <span className="text-sm font-medium">Person photo or video</span>
            </div>
            <p className="mt-1 text-[12px] leading-5 text-muted">
              Choose an included AI presenter, or upload one clear, approved image{infiniteTalkAcceptsVideo ? " or short video" : ""}.
            </p>
            <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">
              Included people
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-6">
              {INFINITETALK_PRESET_PEOPLE.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => setAvatarId(person.id)}
                  className={cn(
                    "card overflow-hidden p-0 text-left transition",
                    avatarId === person.id
                      ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
                      : "hover:border-[var(--border-strong)]",
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={person.imageUrl}
                    alt={person.name}
                    className="aspect-[3/4] w-full object-cover"
                  />
                  <div className="p-2.5">
                    <div className="truncate text-[12px] font-medium">{person.name}</div>
                    <div className="truncate text-[10px] text-muted">AI presenter</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-5 text-[11px] font-medium uppercase tracking-wide text-muted">
              Your people
            </div>
            <label className="mt-4 flex items-start gap-2.5 text-[11px] leading-5 text-muted">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--accent)]"
                checked={avatarConsentAccepted}
                onChange={(event) => setAvatarConsentAccepted(event.target.checked)}
              />
              <span>
                I confirm that I am the person shown, or I have permission to
                animate this person&apos;s likeness with AI.
              </span>
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <input
                ref={avatarPhotoInput}
                type="file"
                accept={
                  infiniteTalkAcceptsVideo
                    ? "image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,.heic,.heif,.mov"
                    : "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                }
                hidden
                onChange={(event) => void uploadInfiniteTalkAvatar(event.target.files)}
              />
              <Button
                size="sm"
                disabled={avatarUploadBusy || !avatarConsentAccepted}
                onClick={() => avatarPhotoInput.current?.click()}
              >
                {avatarUploadBusy ? <Spinner /> : "＋ Upload person"}
              </Button>
              <span className="text-[10px] text-muted">
                iPhone HEIC/HEIF, JPEG, PNG or WebP · up to 10MB
              </span>
            </div>
            {avatarUploadError && (
              <div className="mt-2 text-[12px] text-[#ffb3b3]">
                {avatarUploadError}
              </div>
            )}
            {approvedAvatarUploads.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6">
                {approvedAvatarUploads.map((upload) => (
                  <button
                    key={upload.id}
                    type="button"
                    onClick={() => setAvatarId(upload.id)}
                    className={cn(
                      "card overflow-hidden p-0 text-left transition",
                      avatarId === upload.id
                        ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
                        : "hover:border-[var(--border-strong)]",
                    )}
                  >
                    {upload.kind === "photo" ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={upload.url}
                        alt={upload.filename}
                        className="aspect-[3/4] w-full object-cover"
                      />
                    ) : (
                      <video
                        src={upload.url}
                        className="aspect-[3/4] w-full bg-black object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    )}
                    <div className="p-2.5">
                      <div className="truncate text-[12px] font-medium">
                        {upload.filename}
                      </div>
                      <div className="text-[10px] text-[var(--success)]">
                        Approved
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <label className="mt-5 flex items-start gap-2.5 rounded-xl border border-line bg-white/[0.025] px-3.5 py-3 text-[12px] leading-5">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--accent)]"
                checked={removeAvatarBackground || infiniteTalkBackground.length > 0}
                onChange={(event) => setRemoveAvatarBackground(event.target.checked)}
                disabled={infiniteTalkBackground.length > 0}
              />
              <span>
                <span className="font-medium">Remove the person&apos;s background</span>
                <span className="mt-0.5 block text-[11px] text-muted">
                  {infiniteTalkBackground.length > 0
                    ? "Enabled automatically for background replacement."
                    : "Optional AI cutout before animation. Add a talking background to composite the person into a new scene."}
                </span>
              </span>
            </label>
          </Card>

          <ProductMedia
            items={infiniteTalkBackground}
            onChange={(items) => {
              setInfiniteTalkBackground(items);
              if (items.length > 0) setRemoveAvatarBackground(true);
            }}
            badge="2"
            title="Talking background"
            description="Optional background reference. If omitted, the person's original background is retained."
            allowVideo={false}
            maxItems={1}
          />

          <Card className="p-6">
            <div className="flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                3
              </span>
              <span className="text-sm font-medium">Voice</span>
            </div>
            <p className="mt-1 text-[12px] leading-5 text-muted">
              The selected voice turns the speech into audio before InfiniteTalk animates the person.
            </p>
            <div className="mt-4 max-w-sm">
              <div className="flex gap-2">
                <select
                  value={voiceId}
                  disabled={previewingVoiceId !== null}
                  onChange={(event) => {
                    previewAudio.current?.pause();
                    setVoiceId(event.target.value);
                    setVoiceMessage(null);
                  }}
                  className={cn(inputClass, "min-w-0 flex-1")}
                >
                  <optgroup label="Included voices">
                    {availableVoices.filter((voice) => !voice.id.startsWith("vc_")).map((voice) => (
                      <option key={voice.id} value={voice.id}>
                        {voice.name} · {voice.locale} · {voice.speed}x
                      </option>
                    ))}
                  </optgroup>
                  {clonedVoices.length > 0 && (
                    <optgroup label="My cloned voices">
                      {clonedVoices.map((voice) => (
                        <option key={voice.id} value={voice.id}>{voice.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void previewVoice()}
                  disabled={!voiceId || previewingVoiceId !== null}
                >
                  {previewingVoiceId ? (
                    <><Spinner /> Preparing…</>
                  ) : voicePreviewUrls[voiceId] ? (
                    "↻ Regenerate preview"
                  ) : (
                    "▶ Generate preview"
                  )}
                </Button>
              </div>
              <p className="mt-2 text-[10px] leading-4 text-muted">
                The 23 included language presets use Chatterbox&apos;s official Natural speaker.
                Clone a voice to add a different speaker.
              </p>
              {voicePreviewUrls[voiceId] && (
                <audio
                  key={voicePreviewUrls[voiceId]}
                  ref={previewAudio}
                  className="mt-3 h-9 w-full"
                  controls
                  preload="metadata"
                  src={voicePreviewUrls[voiceId]}
                  onPlay={() => setVoiceMessage(null)}
                  onError={() => setVoiceMessage("Voice preview could not be played")}
                >
                  Your browser does not support audio playback.
                </audio>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="mt-3"
                onClick={() => setShowVoiceClone((value) => !value)}
              >
                ＋ Clone a voice
              </Button>
              {showVoiceClone && (
                <div className="mt-3 space-y-3 rounded-xl border border-line bg-white/[0.025] p-4">
                  <input
                    value={voiceCloneName}
                    onChange={(event) => setVoiceCloneName(event.target.value.slice(0, 80))}
                    className={inputClass}
                    placeholder="Voice name"
                  />
                  <select
                    value={voiceCloneLanguage}
                    onChange={(event) => setVoiceCloneLanguage(event.target.value)}
                    className={inputClass}
                    aria-label="Voice recording language"
                  >
                    {CHATTERBOX_LANGUAGE_OPTIONS.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                  <input
                    ref={voiceCloneInput}
                    type="file"
                    accept="audio/mpeg,audio/mp4,audio/wav,audio/flac,audio/ogg,audio/webm,.mp3,.m4a,.wav,.flac,.ogg,.webm"
                    className="block w-full text-[11px] text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-white"
                    onChange={(event) => setVoiceCloneFile(event.target.files?.[0] ?? null)}
                  />
                  <div className="text-[10px] leading-4 text-muted">
                    Chatterbox reference · MP3, WAV, M4A, FLAC, OGG or WebM · 10–60 seconds recommended · up to 20MB
                  </div>
                  <label className="flex items-start gap-2 text-[11px] leading-5 text-muted">
                    <input
                      type="checkbox"
                      className="mt-1 accent-[var(--accent)]"
                      checked={voiceCloneConsent}
                      onChange={(event) => setVoiceCloneConsent(event.target.checked)}
                    />
                    I own this voice or have explicit permission to clone and use it.
                  </label>
                  <Button
                    size="sm"
                    onClick={() => void cloneVoice()}
                    disabled={!voiceCloneFile || !voiceCloneConsent || voiceCloneBusy}
                  >
                    {voiceCloneBusy ? <><Spinner /> Cloning…</> : "Clone & select"}
                  </Button>
                </div>
              )}
              {voiceMessage && (
                <div className={cn(
                  "mt-2 text-[11px]",
                  voiceMessage.includes("selected") || voiceMessage.includes("ready")
                    ? "text-[var(--success)]"
                    : "text-[#ffb3b3]",
                )}>
                  {voiceMessage}
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                4
              </span>
              <span className="text-sm font-medium">Speech script</span>
            </div>
            <p className="mt-1 text-[12px] leading-5 text-muted">
              Give the script writer a few key facts, then edit every word before generating.
            </p>
            <div className="mt-4 rounded-xl border border-line bg-white/[0.025] p-4">
              <label className="text-[11px] font-medium text-muted">Key information</label>
              <textarea
                value={infiniteTalkBrief}
                onChange={(event) => setInfiniteTalkBrief(event.target.value.slice(0, 2_000))}
                rows={4}
                placeholder="What is being promoted? Who is it for? Key benefits, offer, tone, and call to action…"
                className={cn(inputClass, "mt-2 resize-y leading-6")}
              />
              <Button
                size="sm"
                className={cn("mt-3", busy === "script" && "llm-working disabled:opacity-100")}
                onClick={() =>
                  void requestTokenEstimate(
                    { action: "generate_script", text: infiniteTalkBrief },
                    generateInfiniteTalkSpeech,
                  )
                }
                disabled={!infiniteTalkBrief.trim() || busy !== null || estimating}
              >
                {busy === "script" || estimating ? (
                  <><Spinner /> {estimating ? "Estimating…" : "Writing…"}</>
                ) : (
                  "Generate speech script"
                )}
              </Button>
            </div>
            <label className="mt-4 block text-[11px] font-medium text-muted">
              Editable speech script
            </label>
            <textarea
              value={infiniteTalkSpeech}
              onChange={(event) => setInfiniteTalkSpeech(event.target.value.slice(0, 2000))}
              rows={7}
              placeholder="Type the exact speech here…"
              className={cn(inputClass, "mt-2 resize-y leading-6")}
            />
            <div className="mt-2 flex justify-between text-[11px] text-muted">
              <span>{infiniteTalkSpeech.length}/2,000 characters</span>
              <span>Estimated {estimateInfiniteTalkDuration(infiniteTalkSpeech)}s</span>
            </div>
          </Card>

          {!infiniteTalkAvatarReady && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[12px] leading-5 text-amber-200/90">
              Choose an included person or upload an approved person photo before continuing.
            </div>
          )}

          <Button
            size="lg"
            onClick={prepareInfiniteTalkReview}
            disabled={!infiniteTalkAvatarReady || !infiniteTalkSpeech.trim()}
          >
            Review & generate →
          </Button>
        </div>
      )}

      {infiniteTalkMode && step === 1 && product && script && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Generate your InfiniteTalk video
            </h1>
            <p className="mt-2 text-sm text-muted">
              Review the setup, price, and submit one video.
            </p>
          </div>

          <Card className="p-6">
            <div className="mb-4 text-sm font-medium">Review</div>
            <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
              {[
                [
                  "Person",
                  findInfiniteTalkPresetPerson(avatarId)?.name ??
                    approvedAvatarUploads.find((upload) => upload.id === avatarId)
                      ?.filename ??
                    avatarId,
                ],
                [
                  "Background",
                  infiniteTalkBackground[0]?.filename ?? "Keep source background",
                ],
                ["Voice", availableVoices.find((voice) => voice.id === voiceId)?.name ?? voiceId],
                [
                  "Cutout",
                  infiniteTalkBackground.length > 0 || removeAvatarBackground
                    ? "Background removed"
                    : "Keep person background",
                ],
                ["Speech", `${script.totalDurationSec}s estimated`],
                ["Output", "TikTok · 9:16 · 1 video"],
                [
                  "Engine",
                  infiniteTalkBackground.length > 0
                    ? "TTS → person cutout → InfiniteTalk → background composite → R2"
                    : "TTS → InfiniteTalk → R2",
                ],
              ].map(([key, value]) => (
                <div key={key} className="flex gap-3">
                  <dt className="w-24 shrink-0 text-muted">{key}</dt>
                  <dd className="min-w-0 flex-1 truncate">{value}</dd>
                </div>
              ))}
            </dl>
            {infiniteTalkBackground.length > 0 && (
              <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[12px] leading-5 text-emerald-100/90">
                The person will be cut out, animated with InfiniteTalk, and composited
                onto this background before the final MP4 is uploaded.
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-5">
              <div className="text-sm text-muted">
                {estimatingVideo || !videoEstimate ? (
                  "Calculating charge…"
                ) : (
                  <>
                    {currency === "USD" ? "Charge" : "Estimated charge"}: {formatUsdCents(totalChargeCents)}
                    {currency !== "USD" ? ` · ${exactUsdCents(totalChargeCents)}` : ""}
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setStep(0)}>
                  Back
                </Button>
                <Button
                  onClick={() => void submit()}
                  disabled={busy !== null || estimatingVideo || !videoEstimate}
                >
                  {busy === "submit" ? (
                    <><Spinner /> Submitting…</>
                  ) : videoEstimate ? (
                    `Pay ${formatUsdCents(totalChargeCents)}${currency !== "USD" ? " est." : ""} & generate`
                  ) : (
                    "Calculating price…"
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 1 — 商品 */}
      {!infiniteTalkMode && step === 0 && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              What are you selling?
            </h1>
            <p className="mt-2 text-sm text-muted">
              Paste a product link to auto-extract selling points, or type a
              description by hand.
            </p>
          </div>

          {/* 模块 ① 视频风格（决定脚本语气与渲染工作流） */}
          <Card className="p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                1
              </span>
              <span className="text-sm font-medium">Video style</span>
            </div>
            <div
              className={cn(
                "style-grid grid gap-3 sm:grid-cols-2 lg:grid-cols-3",
                !showAllStyles && (stylePicked ? "is-single" : "is-collapsed"),
              )}
            >
              {templates.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setStyle(s.id);
                    if (
                      s.voiceIds.length > 0 &&
                      !s.voiceIds.includes(voiceId)
                    ) {
                      setVoiceId(s.voiceIds[0]);
                    }
                    if (
                      isFacelessStyle(s.id) ||
                      s.provider?.type === "minimax_h3"
                    ) {
                      setAvatarId("av_none");
                    } else if (s.provider?.type === "infinitetalk") {
                      setAvatarId(approvedAvatarUploads[0]?.id ?? "av_none");
                    } else if (
                      avatarUploads.some((upload) => upload.id === avatarId)
                    ) {
                      setAvatarId(availableAvatars[0]?.id ?? "av_mia");
                    }
                    setScript(null);
                    setStylePicked(true);
                    setShowAllStyles(false);
                  }}
                  className={cn(
                    "card overflow-hidden p-0 text-left transition",
                    style === s.id
                      ? "is-selected border-[var(--accent)] bg-[var(--accent)]/12"
                      : "hover:bg-white/[0.06]",
                  )}
                >
                  <div className="relative">
                    <video
                      src={pickSampleVideo(s.id)}
                      className="h-28 w-full bg-black/40 object-cover"
                      muted
                      loop
                      autoPlay
                      playsInline
                      preload="metadata"
                    />
                    <span className="absolute top-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white/80 backdrop-blur-sm">
                      Sample
                    </span>
                    {style === s.id && (
                      <span className="absolute top-2 left-2 grid size-5 place-items-center rounded-full bg-[var(--accent)] text-[11px] text-white">
                        ✓
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="mt-1.5 text-[12px] leading-5 text-muted">
                      {s.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {templates.length > 2 && (
              // 单卡态时按钮跟在卡片下方；两行/全展开时挂在整块网格的右下
              <div className="mt-3 flex justify-start">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAllStyles((v) => !v)}
                >
                  {showAllStyles
                    ? "Show fewer"
                    : stylePicked
                      ? "Change style →"
                      : `All ${templates.length} styles →`}
                </Button>
              </div>
            )}
          </Card>

          {/* 模块 ② 商品素材 */}
          <ProductMedia
            items={media}
            onChange={onMediaChange}
            badge="2"
            onAnalyze={() =>
              void requestTokenEstimate(
                {
                  action: "analyze_media",
                  media: media.map((item) => ({ kind: item.kind })),
                },
                analyzeMedia,
              )
            }
            analyzing={busy === "media"}
            analyzeDisabled={busy !== null || estimating}
          />

          {/* 模块 ③ 商品链接 */}
          <Card className="p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                3
              </span>
              <span className="text-sm font-medium">Product link</span>
            </div>
            <Field
              label=""
              hint="Amazon / Shopify / TikTok Shop / Shopee / Lazada / Taobao"
            >
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.amazon.com/dp/..."
                className={inputClass}
              />
            </Field>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                className={busy === "scrape" ? "llm-working disabled:opacity-100" : undefined}
                onClick={() =>
                  void requestTokenEstimate(
                    { action: "analyze_link", text: url },
                    () => scrape(undefined, false),
                  )
                }
                disabled={busy !== null || estimating || !url.trim()}
              >
                {busy === "scrape" || estimating ? (
                  <>
                    <Spinner /> {estimating ? "Estimating…" : "Analyzing link…"}
                  </>
                ) : (
                  "Analyze link →"
                )}
              </Button>
              {product && analyzedInput === `${url.trim()}\n${text.trim()}` && (
                <span className="text-[12px] text-[var(--accent-2)]">
                  ✓ Product analyzed
                </span>
              )}
            </div>
          </Card>

          {/* 模块 ④ 手写描述（链接分析结果写入这里） */}
          <Card className="p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
                4
              </span>
              <span className="text-sm font-medium">Or describe it</span>
            </div>
            <Field label="" hint="One selling point per line works best">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder={
                  "Portable espresso maker, self-heating\nCup ready in 3 minutes, heats to 92°C\n480g, fits a backpack side pocket\nOne charge brews 4 cups"
                }
                className={cn(inputClass, "resize-y leading-6")}
              />
            </Field>
          </Card>

          {/* 底部统一进入下一步；未解析的输入会在此自动解析 */}
          <div className="flex flex-wrap items-center gap-4 pl-6">
            <Button
              size="lg"
              className={busy === "scrape" ? "llm-working disabled:opacity-100" : undefined}
              onClick={() => {
                const currentInput = `${url.trim()}\n${text.trim()}`;
                if (product && analyzedInput === currentInput) {
                  void nextFromProduct();
                  return;
                }
                if (url.trim()) {
                  void requestTokenEstimate(
                    { action: "analyze_link", text: url },
                    nextFromProduct,
                  );
                } else if (text.trim()) {
                  void requestTokenEstimate(
                    { action: "analyze_description", text },
                    nextFromProduct,
                  );
                } else {
                  void nextFromProduct();
                }
              }}
              disabled={
                busy !== null ||
                estimating ||
                (!url.trim() && !text.trim() && media.length === 0)
              }
            >
              {busy === "scrape" ? <><Spinner /> Preparing…</> : "Next →"}
            </Button>
            <span className="text-[12px] text-muted">
              Fill in any one of the three — link wins if both link and
              description are given.
            </span>
          </div>
        </div>
      )}

      {/* Step 2 — 形象与平台 */}
      {!infiniteTalkMode && step === 1 && product && (
        <div className="space-y-8">
          <ProductEditor product={product} onChange={setProduct} />

          <div>
            <h2 className="mb-3 text-lg font-semibold">Target platforms</h2>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_PRESETS.filter((p) => p.id !== "taobao").map((p) => {
                const on = platforms.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      setPlatforms((prev) =>
                        on
                          ? prev.filter((x) => x !== p.id).length
                            ? prev.filter((x) => x !== p.id)
                            : prev
                          : [...prev, p.id],
                      )
                    }
                    className={cn(
                      "rounded-full border px-4 py-2 text-[13px] transition",
                      on
                        ? "border-[var(--accent)] bg-[var(--accent)]/16"
                        : "border-line text-muted hover:text-foreground",
                    )}
                  >
                    {p.label}
                    <span className="ml-1.5 text-[11px] opacity-60">
                      {p.ratio}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="mb-1 text-lg font-semibold">Avatar</h2>
            <p className="mb-3 text-[13px] text-muted">
              {presenterlessStyle
                ? h3Style
                  ? "MiniMax H3 generates video and synchronized sound from up to four inspected product images. Reference videos stay disabled until clip duration can be validated."
                  : "Faceless videos use product visuals, voiceover, captions, and music — no presenter appears on camera."
                : infiniteTalkStyle
                  ? infiniteTalkAcceptsVideo
                    ? "InfiniteTalk animates one approved customer photo or video with the selected voice."
                    : "InfiniteTalk animates one approved customer photo with the selected voice."
                : `✦ marks avatars that fit "${activeTemplate?.name ?? style}"`}
            </p>
            {infiniteTalkStyle && (
              <div className="mb-4 rounded-xl border border-line bg-white/[0.03] p-4">
                <div className="text-[13px] font-medium">
                  Upload the person who will speak
                </div>
                <p className="mt-1 text-[11px] leading-5 text-muted">
                  Use one clear, front-facing photo. It will be stored privately
                  and selected automatically after upload.
                </p>
                <label className="mt-3 flex items-start gap-2.5 text-[11px] leading-5 text-muted">
                  <input
                    type="checkbox"
                    className="mt-1 accent-[var(--accent)]"
                    checked={avatarConsentAccepted}
                    onChange={(event) =>
                      setAvatarConsentAccepted(event.target.checked)
                    }
                  />
                  <span>
                    I confirm that I am the person shown, or I have permission
                    to upload and animate this person&apos;s likeness with AI.
                  </span>
                </label>
                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  <input
                    ref={avatarPhotoInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    hidden
                    onChange={(event) =>
                      void uploadInfiniteTalkAvatar(event.target.files)
                    }
                  />
                  <Button
                    size="sm"
                    disabled={avatarUploadBusy || !avatarConsentAccepted}
                    onClick={() => avatarPhotoInput.current?.click()}
                  >
                    {avatarUploadBusy ? <Spinner /> : "＋ Upload avatar photo"}
                  </Button>
                  <span className="text-[10px] text-muted">
                    iPhone HEIC/HEIF, JPEG, PNG or WebP · up to 10MB
                  </span>
                </div>
                {avatarUploadError && (
                  <div className="mt-2 text-[12px] text-[#ffb3b3]">
                    {avatarUploadError}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              {!presenterlessStyle && !infiniteTalkStyle && availableAvatars.map((a) => {
                const fits = a.styles.includes(style);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAvatarId(a.id)}
                    className={cn(
                      "card overflow-hidden p-0 text-left transition",
                      avatarId === a.id
                        ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
                        : "hover:border-[var(--border-strong)]",
                    )}
                  >
                    <AvatarVideo
                      avatarId={a.id}
                      className="aspect-[3/4] w-full"
                    />
                    <div className="p-2.5">
                      <div className="truncate text-[12px] font-medium">
                        {fits && (
                          <span className="text-[var(--accent-2)]">✦ </span>
                        )}
                        {a.name}
                      </div>
                      <div className="text-[10px] text-muted">{a.locale}</div>
                    </div>
                  </button>
                );
              })}
              {infiniteTalkStyle && approvedAvatarUploads.map((upload) => (
                <button
                  key={upload.id}
                  type="button"
                  onClick={() => setAvatarId(upload.id)}
                  className={cn(
                    "card overflow-hidden p-0 text-left transition",
                    avatarId === upload.id
                      ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
                      : "hover:border-[var(--border-strong)]",
                  )}
                >
                  {upload.kind === "photo" ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={upload.url}
                      alt={upload.filename}
                      className="aspect-[3/4] w-full object-cover"
                    />
                  ) : (
                    <video
                      src={upload.url}
                      className="aspect-[3/4] w-full bg-black object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  )}
                  <div className="p-2.5">
                    <div className="truncate text-[12px] font-medium">
                      {upload.filename}
                    </div>
                    <div className="text-[10px] text-[var(--success)]">
                      Consent approved
                    </div>
                  </div>
                </button>
              ))}
              {/* 无数字人出镜：旁白 + 商品画面（Product Showcase 引擎，二期接通） */}
              {!infiniteTalkStyle && <button
                type="button"
                onClick={() => setAvatarId("av_none")}
                className={cn(
                  "card overflow-hidden p-0 text-left transition",
                  avatarId === "av_none"
                    ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40"
                    : "hover:border-[var(--border-strong)]",
                )}
              >
                <div className="grid aspect-[3/4] w-full place-items-center bg-white/[0.04]">
                  <div className="text-center">
                    <div className="text-3xl">📦</div>
                    <div className="mt-2 px-2 text-[10px] leading-4 text-muted">
                      Product shots
                      <br />+ voiceover
                    </div>
                  </div>
                </div>
                <div className="p-2.5">
                  <div className="truncate text-[12px] font-medium">
                    No avatar
                  </div>
                  <div className="text-[10px] text-muted">Faceless</div>
                </div>
              </button>}
            </div>
            {infiniteTalkStyle && approvedAvatarUploads.length === 0 && (
              <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[12px] leading-5 text-amber-200/90">
                No compatible approved avatar media yet. Upload a person photo
                above, or manage existing media on the{` `}
                <a className="underline" href="/studio/avatars">
                  Avatars page
                </a>
                .
              </div>
            )}
          </div>

          <div className="max-w-sm">
            <Field label="Voice">
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className={inputClass}
              >
                {availableVoices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} · {v.locale} · {v.speed}x
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setStep(0)}>
              Back
            </Button>
            <Button
              disabled={!infiniteTalkAvatarReady}
              onClick={async () => {
                if (script) {
                  setStep(2);
                  return;
                }
                await requestTokenEstimate(
                  { action: "generate_script", product },
                  async () => {
                    setStep(2);
                    await genScript();
                  },
                );
              }}
            >
              Generate script →
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — 脚本 */}
      {!infiniteTalkMode && step === 2 && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Script & storyboard
              </h1>
              <p className="mt-2 text-sm text-muted">
                Edit any scene's narration or caption — changes stay local to
                that scene.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className={busy === "script" ? "llm-working disabled:opacity-100" : undefined}
              onClick={() => {
                void requestTokenEstimate(
                  { action: "generate_script", product },
                  genScript,
                );
              }}
              disabled={busy !== null}
            >
              {busy === "script" ? <Spinner /> : "↻"} Rewrite
            </Button>
          </div>

          {busy === "script" && !script && <ScriptSkeleton />}

          {script && (
            <>
              <Card className="p-5">
                <div className="mb-2 flex items-center gap-2">
                  <Badge tone="accent">3-second hook</Badge>
                  <span className="text-[11px] text-muted">
                    Decides watch-through — the single most valuable line to
                    edit
                  </span>
                </div>
                <textarea
                  value={script.hook}
                  onChange={(e) =>
                    setScript({ ...script, hook: e.target.value })
                  }
                  rows={2}
                  className={cn(inputClass, "text-[15px] leading-6")}
                />
              </Card>

              <div className="space-y-3">
                {script.scenes.map((sc, i) => (
                  <SceneRow
                    key={sc.id}
                    index={i}
                    scene={sc}
                    ratio={
                      PLATFORM_PRESETS.find((p) => p.id === platforms[0])
                        ?.ratio ?? "9:16"
                    }
                    onChange={(next) =>
                      setScript({
                        ...script,
                        scenes: script.scenes.map((x) =>
                          x.id === sc.id ? next : x,
                        ),
                        totalDurationSec: script.scenes.reduce(
                          (n, x) =>
                            n +
                            (x.id === sc.id ? next.durationSec : x.durationSec),
                          0,
                        ),
                      })
                    }
                    onRemove={
                      script.scenes.length > 2
                        ? () =>
                            setScript({
                              ...script,
                              scenes: script.scenes.filter(
                                (x) => x.id !== sc.id,
                              ),
                              totalDurationSec:
                                script.totalDurationSec - sc.durationSec,
                            })
                        : undefined
                    }
                  />
                ))}
              </div>

              <Card className="flex items-center justify-between px-5 py-4 text-sm">
                <span className="text-muted">
                  {script.scenes.length} scenes
                </span>
                <span>
                  Estimated length{" "}
                  <span className="font-mono font-medium">
                    {script.totalDurationSec}s
                  </span>
                </span>
              </Card>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button onClick={() => setStep(3)}>Next →</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 4 — 生成 */}
      {!infiniteTalkMode && step === 3 && product && script && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Batch generate
            </h1>
            <p className="mt-2 text-sm text-muted">
              Produce several variants and let the data pick the winner — never
              launch just one.
            </p>
          </div>

          <Card className="space-y-6 p-6">
            <Field
              label={`Variants: ${variants} per platform`}
              hint="Up to 50"
            >
              <input
                type="range"
                min={1}
                max={50}
                value={variants}
                onChange={(e) => setVariants(Number(e.target.value))}
                className="w-full accent-[var(--accent)]"
              />
            </Field>

            <div className="space-y-3">
              <Toggle
                checked={varyHooks}
                onChange={setVaryHooks}
                label="Different hook per variant"
                desc="The hook is the biggest watch-through variable — keep this on"
              />
              <Toggle
                checked={
                  avatarId === "av_none" || infiniteTalkStyle
                    ? false
                    : varyAvatars
                }
                onChange={setVaryAvatars}
                disabled={avatarId === "av_none" || infiniteTalkStyle}
                label="Rotate avatars"
                desc={
                  avatarId === "av_none"
                    ? "Not available — you picked No avatar"
                    : infiniteTalkStyle
                      ? "InfiniteTalk keeps the approved source person fixed"
                    : "Same script, different personas — find which one your audience buys from"
                }
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-4 text-sm font-medium">Review</div>
            <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
              {[
                ["Product", product.title],
                ["Style", activeTemplate?.name ?? style],
                [
                  "Avatar",
                  avatarId === "av_none"
                    ? "None — product shots + voiceover"
                    : infiniteTalkStyle
                      ? (approvedAvatarUploads.find(
                          (upload) => upload.id === avatarId,
                        )?.filename ?? avatarId)
                      : (avatars.find((a) => a.id === avatarId)?.name ?? avatarId),
                ],
                [
                  "Voice",
                  voices.find((v) => v.id === voiceId)?.name ?? voiceId,
                ],
                [
                  "Platforms",
                  platforms
                    .map(
                      (p) =>
                        PLATFORM_PRESETS.find((x) => x.id === p)?.label ?? p,
                    )
                    .join(", "),
                ],
                ["Length", `${videoEstimate?.durationSec ?? script.totalDurationSec}s`],
                [
                  "Engine",
                  activeTemplate?.provider?.type === "seedance"
                    ? "Seedance 2.0"
                    : activeTemplate?.provider?.type === "heygen"
                      ? "HeyGen Avatar"
                      : activeTemplate?.provider?.type === "minimax_h3"
                        ? "MiniMax H3"
                        : activeTemplate?.provider?.type === "infinitetalk"
                          ? "InfiniteTalk"
                      : (activeTemplate?.provider?.type ?? "Configured workflow"),
                ],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <dt className="w-20 shrink-0 text-muted">{k}</dt>
                  <dd className="min-w-0 flex-1">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 flex items-center justify-between gap-4 border-t border-line pt-5">
              <div className="text-sm">
                Total output:{" "}
                <span className="font-semibold text-[var(--accent-2)]">
                  {totalOutputs}
                </span>{" "}
                videos
                <span className="ml-3 text-muted">
                  {estimatingVideo || !videoEstimate ? (
                    "Calculating charge…"
                  ) : (
                    <>{currency === "USD" ? "Charge" : "Estimated charge"}: {formatUsdCents(totalChargeCents)}{currency !== "USD" ? ` · ${exactUsdCents(totalChargeCents)}` : ""}</>
                  )}
                </span>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button
                  onClick={() => void submit()}
                  disabled={
                    busy !== null ||
                    estimatingVideo ||
                    !videoEstimate ||
                    !infiniteTalkAvatarReady
                  }
                >
                  {busy === "submit" ? (
                    <>
                      <Spinner /> Submitting…
                    </>
                  ) : (
                    videoEstimate
                      ? `Pay ${formatUsdCents(totalChargeCents)}${currency !== "USD" ? " est." : ""} & generate`
                      : "Calculating price…"
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/** 手填模式走同一个接口，只是传 text */
async function postText(text: string): Promise<Product> {
  const res = await fetch("/api/scrape", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function ProductEditor({
  product,
  onChange,
}: {
  product: Product;
  onChange: (p: Product) => void;
}) {
  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex gap-2">
          {product.images.slice(0, 2).map((src) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={src}
              src={src}
              alt=""
              className="size-20 rounded-xl object-cover"
            />
          ))}
        </div>
        <div className="min-w-[240px] flex-1 space-y-3">
          <input
            value={product.title}
            onChange={(e) => onChange({ ...product, title: e.target.value })}
            className={cn(inputClass, "text-[15px] font-medium")}
          />
          <div className="flex gap-3">
            <input
              value={product.price ?? ""}
              onChange={(e) => onChange({ ...product, price: e.target.value })}
              placeholder="Price"
              className={cn(inputClass, "w-32")}
            />
            <input
              value={product.audience ?? ""}
              onChange={(e) =>
                onChange({ ...product, audience: e.target.value })
              }
              placeholder="Target audience"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-2.5 flex items-center justify-between">
          <span className="text-sm font-medium">Selling points</span>
          <button
            type="button"
            onClick={() =>
              onChange({
                ...product,
                sellingPoints: [...product.sellingPoints, ""],
              })
            }
            className="text-[12px] text-muted transition hover:text-foreground"
          >
            + Add one
          </button>
        </div>
        <div className="space-y-2">
          {product.sellingPoints.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-5 shrink-0 text-center font-mono text-[11px] text-muted">
                {i + 1}
              </span>
              <input
                value={p}
                onChange={(e) =>
                  onChange({
                    ...product,
                    sellingPoints: product.sellingPoints.map((x, j) =>
                      j === i ? e.target.value : x,
                    ),
                  })
                }
                className={cn(inputClass, "py-2.5 text-[13px]")}
              />
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...product,
                    sellingPoints: product.sellingPoints.filter(
                      (_, j) => j !== i,
                    ),
                  })
                }
                className="shrink-0 px-2 text-muted transition hover:text-[var(--danger)]"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[12px] text-muted">
          The first 3 points become scenes, in this order.
        </p>
      </div>
    </Card>
  );
}

function SceneRow({
  index,
  scene,
  ratio,
  onChange,
  onRemove,
}: {
  index: number;
  scene: Scene;
  ratio: string;
  onChange: (s: Scene) => void;
  onRemove?: () => void;
}) {
  return (
    <Card className="flex flex-wrap gap-5 p-5">
      <div className="w-24 shrink-0">
        <div
          className="w-full overflow-hidden rounded-lg bg-white/6"
          style={{ aspectRatio: RATIO_CSS[ratio] ?? "9 / 16" }}
        >
          {scene.imageUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={scene.imageUrl}
              alt=""
              className="size-full object-cover"
            />
          )}
        </div>
        <div className="mt-2 text-center font-mono text-[11px] text-muted">
          #{index + 1}
        </div>
      </div>

      <div className="min-w-[260px] flex-1 space-y-3">
        <div>
          <div className="mb-1.5 text-[11px] text-muted">Narration</div>
          <textarea
            value={scene.narration}
            onChange={(e) => onChange({ ...scene, narration: e.target.value })}
            rows={2}
            className={cn(inputClass, "py-2.5 text-[13px] leading-6")}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[180px] flex-1">
            <div className="mb-1.5 text-[11px] text-muted">
              On-screen caption
            </div>
            <input
              value={scene.overlay ?? ""}
              onChange={(e) => onChange({ ...scene, overlay: e.target.value })}
              className={cn(inputClass, "py-2.5 text-[13px]")}
            />
          </div>
          <div className="w-24">
            <div className="mb-1.5 text-[11px] text-muted">Length (s)</div>
            <input
              type="number"
              min={1}
              max={20}
              value={scene.durationSec}
              onChange={(e) =>
                onChange({
                  ...scene,
                  durationSec: Math.max(1, Number(e.target.value) || 1),
                })
              }
              className={cn(inputClass, "py-2.5 text-[13px]")}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted">Visual: {scene.visual}</p>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 text-[12px] text-muted transition hover:text-[var(--danger)]"
            >
              Remove scene
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  desc,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  desc: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 rounded-xl border border-line p-4 text-left transition hover:bg-white/4 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={cn(
          "mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border text-[11px] transition",
          checked
            ? "border-transparent bg-[var(--accent)] text-white"
            : "border-line-strong",
        )}
      >
        {checked ? "✓" : ""}
      </span>
      <span>
        <span className="block text-sm">{label}</span>
        <span className="mt-0.5 block text-[12px] text-muted">{desc}</span>
      </span>
    </button>
  );
}

function ScriptSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="shimmer relative h-28 overflow-hidden" />
      ))}
    </div>
  );
}
